import { useSelector } from "react-redux";

// Main Permission Hook using Redux
export const usePermissions = () => {
  const user = useSelector((state) => state.auth);
  console.log(user);

  // Check if user has specific permission
  const hasPermission = (permissionId) => {
    if (!user.isAuthenticated) return false;

    // Admin bypass - if role is 'admin', grant all permissions
    if (user.role === "admin") return true;

    // Check if user has the specific permission
    return user.credential?.permissions?.includes(permissionId) || false;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionIds = []) => {
    if (!user.isAuthenticated) return false;

    // Admin bypass
    if (user.role === "admin") return true;

    return permissionIds.some((permissionId) =>
      user.credential?.permissions?.includes(permissionId)
    );
  };

  // Check if user has all specified permissions
  const hasAllPermissions = (permissionIds = []) => {
    if (!user.isAuthenticated) return false;

    // Admin bypass
    if (user.role === "admin") return true;

    return permissionIds.every((permissionId) =>
      user.credential?.permissions?.includes(permissionId)
    );
  };

  // Check if user has specific role
  const hasRole = (role) => {
    if (!user.isAuthenticated) return false;
    return user.role === role;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user.role === "admin";
  };

  // Get all user permissions
  const getUserPermissions = () => {
    if (!user.isAuthenticated) return [];

    // If admin, return 'all' or you can define a comprehensive permissions array
    if (user.role === "admin") {
      return "all";
    }

    return user.credential?.permissions || [];
  };

  return {
    user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    getUserPermissions,
    isAuthenticated: user.isAuthenticated,
    isLoading: user.isLoading,
  };
};

// Permission Guard Component
export const PermissionGuard = ({
  children,
  permission,
  permissions,
  requireAll = false,
  role,
  fallback = null,
  showLoader = false,
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isLoading,
  } = usePermissions();

  // Show loading state if needed
  if (showLoader && isLoading) {
    return <div className="loading">Loading...</div>;
  }

  // Check role first
  if (role && !hasRole(role)) {
    return fallback;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    return fallback;
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return fallback;
    }
  }

  return children;
};

// Higher Order Component for route protection
export const withPermissionCheck = (
  WrappedComponent,
  requiredPermissions = [],
  options = {}
) => {
  return function PermissionProtectedComponent(props) {
    const { hasAnyPermission, hasAllPermissions, isAdmin, isLoading } =
      usePermissions();
    const {
      requireAll = false,
      adminBypass = true,
      showLoader = false,
    } = options;

    // Show loading if needed
    if (showLoader && isLoading) {
      return <div className="loading">Loading...</div>;
    }

    // Admin bypass
    if (adminBypass && isAdmin()) {
      return <WrappedComponent {...props} />;
    }

    // Check permissions
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return (
        <div className="access-denied">
          <h3>غير مسموح بالدخول</h3>
          <p>ليس لديك صلاحية لعرض هذا القسم.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};
